import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import {
  createAgreementCompany,
  createAgreementMagazine,
  createAgreementPromocodeBatch,
  createAgreementPushNotification,
  createAgreementStaff,
  createBanner,
  createBulkSubscriptions,
  createMapIntegrationPlanStaff,
  createSubscriptions,
  createSubscriptionsCode,
  deleteAgreementBanner,
  deleteAgreementPushNotification,
  deleteAgreementStaff,
  deleteCpfsSubscriptions,
  deleteMapIntegrationPlanStaff,
  deleteSubscription,
  getAllMapIntegrationPlansStaff,
  getAgreementCompanies,
  getAgreementData,
  getAgreementInfo,
  getAgreementLandingInfo,
  getAgreementMagazines,
  getAgreementOnlineCompanies,
  getAgreementPermissions,
  getAgreementPlans,
  getAgreementPromocodeBatchs,
  getAgreementPromocodeBatchsAggByTags,
  getAgreementPromocodesFromBatch,
  getAgreementPushNotifications,
  getAgreementStaffs,
  getBanners,
  getDefaultProductsV2,
  getImportation,
  getModules,
  getPlansByIntegrationId,
  getPlansErp,
  syncPlansErp,
  getProducts,
  getReceivedBooksStaff,
  getProductsV2,
  getReport,
  getReports,
  getReportsList,
  getSubscriptions,
  getUsage,
  getUsers,
  normalizeErpPlan,
  sendAgreementNotificationEmail,
  testIntegrationVinculate,
  toggleAgreementOnlineCompany,
  toggleBanner,
  updateAgreementInfo,
  updateAgreementLandingInfo,
  type IAgreementLandingInfoPayload,
  updateAgreementStaff,
  getBookStaffs,
  getMonthlyReportBooks,
  getBookStaffDownload,
  getSyncClientsMetadata,
  type ISyncClientsMetadataListResponse,
  getSyncClientMetadataDetail,
  generateSyncClientsMetadataReport,
  getFinancialClientsByCategory,
  getFinancialPlansByCategory,
} from './api.ts';
import {
  AgreementNotificationSettings,
  AgreementNotificationSettingsPayload,
  IAgreement,
  IAgreementEmail,
  IErpPlansData,
  IAgreementLanding,
  IAgreementNotification,
  IAgreementPromocode,
  IAgreementPromocodeBatch,
  ICreateAgreementPromocodeBatch,
  ICreateAgreementStaff,
  ICreateBannerData,
  ICreateMapIntegrationPlanPayload,
  ICreateSubscriptionData,
  IDeleteCPFSubscriptionData,
  IImportation,
  IMapIntegrationPlanItem,
  IModules,
  IPlan,
  IProduct,
  IPromocodeByTag,
  IReport,
  IReceivedBookStaffListResponse,
  ISubscription,
  IUsage,
  IMonthlyReportResponse,
  IIntegrationDataDisplayResponse,
  IReportItem,
  ISyncReportResponse,
  ProductERP,
  IBookStaffDownload,
  ISyncClientMetadata,
  ISyncClientMetadataDetail,
  ISyncClientsInvalidResponse,
  ISyncClientsMetadataExportPayload,
  IFinancialClientsByCategory,
  IFinancialPage,
  IFinancialPlansByCategory,
} from './types.ts';
import { IBanner, IBook, ICompany, IUser, PaginatedResponse, RequestParameters } from '../types';

export enum DashboardHooks {
  Report = 'report',
  Reports = 'reports',
  Agreement = 'agreement',
  AgreementInfo = 'agreement-info',
  Subscriptions = 'subscriptions',
  Plans = 'plans',
  Products = 'products',
  Users = 'users',
  Modules = 'modules',
  Usage = 'usage',
  Banners = 'banners',
  Company = 'company',
  OnlineCompany = 'online-company',
  PromocodeBatch = 'promocode-batch',
  PromocodeAggByTags = 'promocode-batch-agg-by-tags',
  Magazine = 'magazine',
  PushNotifications = 'push-notifications',
  Staff = 'staff',
  LandingInfo = 'landing-info',
  Permissions = 'permissions',
  Importations = 'importations',
  PlansIntegration = 'plans-integration',
  ReceivedBookStaff = 'received-book-staff',
  BookStaffs = 'book-staffs',
  MonthlyReportBooks = 'monthly-report-books',
  ErpPlans = 'erp-plans',
  AllMapIntegrationPlans = 'all-map-integration-plans',
  SyncClientsMetadata = 'sync-clients-metadata',
  FinancialClientsByCategory = 'financial-clients-by-category',
  FinancialPlansByCategory = 'financial-plans-by-category',
}

export function useGetImportations(params: RequestParameters = {}): UseQueryResult<PaginatedResponse<IImportation>> {
  return useQuery([DashboardHooks.Importations, params], () => getImportation(params));
}

export function useReport(year: number | string): UseQueryResult<IReport> {
  return useQuery([DashboardHooks.Report, year], () => getReport(year));
}

export function useModulesSuperApp(): UseQueryResult<IModules[]> {
  return useQuery([DashboardHooks.Modules], () => getModules(), {
    refetchOnMount: false,
    refetchInterval: 30_000, // Refetch a cada 30s para manter Super App atualizado (background, sem reload visível)
  });
}

export function useProductsAndPlans(): UseQueryResult<IProduct[]> {
  return useQuery([DashboardHooks.Products], () => getProducts());
}

export function useAgreementData(): UseQueryResult<IAgreement> {
  return useQuery([DashboardHooks.Agreement], () => getAgreementData());
}

export function useAgreementInfo(): UseQueryResult<AgreementNotificationSettings> {
  return useQuery([DashboardHooks.AgreementInfo], () => getAgreementInfo());
}

export function useAgreementDataMutation(options: UseMutationOptions<IAgreement>) {
  return useMutation(() => getAgreementData(), options);
}

export function useUpdateAgreementInfoMutation(
  options: UseMutationOptions<
    AgreementNotificationSettings,
    unknown,
    AgreementNotificationSettingsPayload
  >
) {
  return useMutation((data: AgreementNotificationSettingsPayload) => updateAgreementInfo(data), options);
}

export function useSubscriptions(params: RequestParameters): UseQueryResult<PaginatedResponse<ISubscription>> {
  return useQuery([DashboardHooks.Subscriptions, params], () => getSubscriptions(params));
}

export function useUsers(params: RequestParameters): UseQueryResult<PaginatedResponse<IUser>> {
  return useQuery([DashboardHooks.Users, params], () => getUsers(params));
}

export function useAgreementBanners(): UseQueryResult<PaginatedResponse<IBanner>> {
  return useQuery([DashboardHooks.Banners], () => getBanners());
}

export function useDeleteAgreementBannerMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => deleteAgreementBanner(id), options);
}

export function useReports(): UseQueryResult<PaginatedResponse<IUsage>> {
  return useQuery([DashboardHooks.Reports], () => getReports());
}

export function useReportsList(
  params: RequestParameters = {}
): UseQueryResult<PaginatedResponse<IReportItem>> {
  return useQuery([DashboardHooks.Reports, params], () => getReportsList(params), {
    refetchInterval: 30_000, // Refetch a cada 30 segundos para atualizar status dos relatórios
  });
}

export function useErpPlansReports(): UseQueryResult<PaginatedResponse<IReportItem>> {
  return useQuery(
    [DashboardHooks.Reports, 'erp-plans-dashboard'],
    () =>
      getReportsList({
        type: 'erp-plans-dashboard',
        page_size: 5,
        ordering: '-created_at',
      })
  );
}

export function useUsage(params: RequestParameters): UseQueryResult<PaginatedResponse<IUsage>> {
  return useQuery([DashboardHooks.Usage, params], () => getUsage(params));
}

export function useAgreementPlans(params: RequestParameters = {}): UseQueryResult<{
  results: IPlan[];
}> {
  return useQuery([DashboardHooks.Plans, params], () => getAgreementPlans(params), {
    refetchOnMount: false,
  });
}

export function useProductsV2(
  params: RequestParameters = {},
  options?: { enabled?: boolean; refetchOnMount?: boolean }
): UseQueryResult<{
  count: number;
  next: string | null;
  previous: string | null;
  results: Product[];
}> {
  return useQuery([DashboardHooks.Products, params], () => getProductsV2(params), options);
}

export function useDefaultProductsV2(params: RequestParameters = {}): UseQueryResult<IProduct[]> {
  return useQuery([DashboardHooks.Products, params], () => getDefaultProductsV2(params));
}

export function useAgreementPlansByIntegrationId(
  params: RequestParameters = {},
  options?: { enabled?: boolean; refetchOnMount?: boolean }
): UseQueryResult<{
  count: number;
  next: string | null;
  previous: string | null;
  results: ProductERP[];
}> {
  return useQuery([DashboardHooks.PlansIntegration, params], () => getPlansByIntegrationId(params), options);
}

export function useAgreementCompanies(params: RequestParameters = {}): UseQueryResult<PaginatedResponse<ICompany>> {
  return useQuery([DashboardHooks.Company, params], () => getAgreementCompanies(params));
}

export function useAgreementOnlineCompanies(
  params: RequestParameters = {}
): UseQueryResult<PaginatedResponse<ICompany>> {
  return useQuery([DashboardHooks.OnlineCompany, params], () => getAgreementOnlineCompanies(params));
}



export function useReceivedBooksStaff(
  params: RequestParameters
): UseQueryResult<IReceivedBookStaffListResponse> {
  return useQuery([DashboardHooks.ReceivedBookStaff, params], () => getReceivedBooksStaff(params), { retry: false });
}

/** Resolve com a resposta completa (results + statistics do backend), para que as
 * telas exibam os totais agregados pela API em vez de recontar no cliente. */
export function useExportReceivedBooksStaffMutation(
  options: UseMutationOptions<IReceivedBookStaffListResponse, unknown, RequestParameters>
) {
  return useMutation(async (params: RequestParameters) => getReceivedBooksStaff(params), options);
}

export function useGetBookStaffs(
  params: RequestParameters
): UseQueryResult<PaginatedResponse<IBook>> {
  return useQuery([DashboardHooks.BookStaffs, params], () => getBookStaffs(params));
}

export function useMonthlyReportBooks(
  params: RequestParameters = {}
): UseQueryResult<IMonthlyReportResponse> {
  return useQuery([DashboardHooks.MonthlyReportBooks, params], () => getMonthlyReportBooks(params), { retry: false });
}

export function useBookStaffDownloadMutation(
  options?: UseMutationOptions<IBookStaffDownload, unknown, number | string>
) {
  return useMutation((bookId: number | string) => getBookStaffDownload(bookId), options);
}

export function useTestIntegrationVinculateMutation(
  options: UseMutationOptions<IIntegrationDataDisplayResponse, unknown, { agreementId: number; cpf: string }>
) {
  return useMutation(({ agreementId, cpf }: { agreementId: number; cpf: string }) =>
    testIntegrationVinculate(agreementId, cpf),
    options
  );
}

export function useDeleteSubscriptionMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => deleteSubscription(id), options);
}

export function useCreateSubscriptionMutation(options: UseMutationOptions<unknown, unknown, ICreateSubscriptionData>) {
  return useMutation(async (data: ICreateSubscriptionData) => {
    const { planID, cpfs, external_code, cpfs_bulk, external_codes_bulk } = data;

    // New bulk format
    if (cpfs_bulk?.length) {
      return createBulkSubscriptions({ cpfs: cpfs_bulk });
    } else if (external_codes_bulk?.length) {
      return createBulkSubscriptions({ external_codes: external_codes_bulk });
    }

    // Legacy format
    if (cpfs?.length && planID) {
      return createSubscriptions(cpfs, planID);
    } else if (external_code?.length && planID) {
      return createSubscriptionsCode(external_code, planID);
    }

    return Promise.reject(new Error('Invalid subscription data'));
  }, options);
}

export function useDeleteCPFSMutation(options: UseMutationOptions<unknown, unknown, IDeleteCPFSubscriptionData>) {
  return useMutation(({ cpfs }: IDeleteCPFSubscriptionData) => deleteCpfsSubscriptions(cpfs), options);
}

export function useToggleBannerMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => toggleBanner(id), options);
}

export function useToggleAgreementOnlineCompany(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => toggleAgreementOnlineCompany(id), options);
}

export function useAgreementPromocodeBatches(
  params: RequestParameters = {}
): UseQueryResult<PaginatedResponse<IAgreementPromocodeBatch>> {
  return useQuery([DashboardHooks.PromocodeBatch, params], () => getAgreementPromocodeBatchs(params));
}

export function useAgreementPromocodeBatchesAggByTag(
  params: RequestParameters = {}
): UseQueryResult<PaginatedResponse<IPromocodeByTag>> {
  return useQuery([DashboardHooks.PromocodeAggByTags, params], () => getAgreementPromocodeBatchsAggByTags(params));
}

export function useAgreementMagazines(params: RequestParameters = {}) {
  return useQuery([DashboardHooks.Magazine, params], () => getAgreementMagazines(params));
}

export function useAgreementPushNotifications() {
  return useQuery([DashboardHooks.PushNotifications], () => getAgreementPushNotifications());
}

export function useAgreementStaffs(params: RequestParameters = {}) {
  return useQuery([DashboardHooks.Staff, params], () => getAgreementStaffs(params));
}

export function useAgreementLandingInfo() {
  return useQuery([DashboardHooks.LandingInfo], () => getAgreementLandingInfo());
}

export function useAgreementPermissions() {
  return useQuery([DashboardHooks.Permissions], () => getAgreementPermissions());
}

export function useCreateBannerMutation(options: UseMutationOptions<unknown, unknown, ICreateBannerData>) {
  return useMutation((data: ICreateBannerData) => createBanner(data), options);
}

export function useCreateAgreementPushNotificationMutation(
  options: UseMutationOptions<unknown, unknown, IAgreementNotification>
) {
  return useMutation((data: IAgreementNotification) => createAgreementPushNotification(data), options);
}

export function useDeleteAgreementPushNotificationMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => deleteAgreementPushNotification(id), options);
}

export function useDeleteAgreementStaffMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((id: number) => deleteAgreementStaff(id), options);
}

export function useUpdateAgreementLandingInfoMutation(
  options: UseMutationOptions<IAgreementLanding, unknown, Partial<IAgreementLandingInfoPayload>>
) {
  const queryClient = useQueryClient();
  return useMutation((data: Partial<IAgreementLandingInfoPayload>) => updateAgreementLandingInfo(data), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Central do Cliente e modal de Configurações leem a mesma query.
      queryClient.setQueryData([DashboardHooks.LandingInfo], data);
      return options.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateAgreementPromocodeBatchMutation(
  options: UseMutationOptions<unknown, unknown, ICreateAgreementPromocodeBatch>
) {
  return useMutation((data: ICreateAgreementPromocodeBatch) => createAgreementPromocodeBatch(data), options);
}

export function useCreateAgreementMagazineMutation(options: UseMutationOptions<unknown, unknown, number>) {
  return useMutation((agreementID: number) => createAgreementMagazine(agreementID), options);
}

export function useCreateAgreementStaffMutation(options: UseMutationOptions<unknown, unknown, ICreateAgreementStaff>) {
  return useMutation((data: ICreateAgreementStaff) => createAgreementStaff(data), options);
}

export function useUpdateAgreementStaffMutation(options: UseMutationOptions<unknown, unknown, ICreateAgreementStaff>) {
  return useMutation((data: ICreateAgreementStaff) => updateAgreementStaff(data), options);
}

export function useSendAgreementEmailMutation(options: UseMutationOptions<unknown, unknown, IAgreementEmail>) {
  return useMutation((data: IAgreementEmail) => sendAgreementNotificationEmail(data), options);
}

export function useGetAgreementPromocodesFromBatchMutation(
  options: UseMutationOptions<IAgreementPromocode[], unknown, string>
) {
  return useMutation((data: string) => getAgreementPromocodesFromBatch(data), options);
}

export function useCreateAgreementCompanyMutation(
  options: UseMutationOptions<unknown, unknown, Record<string, unknown>>
) {
  return useMutation((data: Record<string, unknown>) => createAgreementCompany(data), options);
}

export function useErpPlans(): UseQueryResult<IErpPlansData> {
  return useQuery(
    [DashboardHooks.ErpPlans],
    async () => {
      const response = await getPlansErp();
      return { plans: response.plans.map(normalizeErpPlan), report: response.report };
    },
    { refetchOnMount: false }
  );
}

export function useSyncErpPlansMutation(
  options?: UseMutationOptions<ISyncReportResponse, unknown, void>
) {
  return useMutation(async () => syncPlansErp(), options);
}

export function useAllMapIntegrationPlans(): UseQueryResult<IMapIntegrationPlanItem[]> {
  return useQuery([DashboardHooks.AllMapIntegrationPlans], () => getAllMapIntegrationPlansStaff(), {
    refetchOnMount: false,
  });
}

export function useCreateMapIntegrationPlanMutation(
  options: UseMutationOptions<IMapIntegrationPlanItem, unknown, ICreateMapIntegrationPlanPayload>
) {
  return useMutation((data: ICreateMapIntegrationPlanPayload) => createMapIntegrationPlanStaff(data), options);
}

export function useDeleteMapIntegrationPlanMutation(options?: UseMutationOptions<void, unknown, number>) {
  return useMutation((id: number) => deleteMapIntegrationPlanStaff(id), options);
}

export function useSyncClientsMetadata(
  params: RequestParameters
): UseQueryResult<ISyncClientsMetadataListResponse> {
  return useQuery(
    [DashboardHooks.SyncClientsMetadata, params],
    () => getSyncClientsMetadata(params),
    { retry: false }
  );
}

export function useSyncClientMetadataDetail(
  id: number | string,
  options?: { enabled?: boolean }
): UseQueryResult<ISyncClientMetadataDetail | ISyncClientsInvalidResponse> {
  return useQuery(
    [DashboardHooks.SyncClientsMetadata, 'detail', id],
    () => getSyncClientMetadataDetail(id),
    { enabled: options?.enabled ?? true, retry: false }
  );
}

export function useFinancialClientsByCategory(filters: {
  page: number;
  year?: number;
  month?: number;
}): UseQueryResult<IFinancialPage<IFinancialClientsByCategory>> {
  return useQuery(
    [DashboardHooks.FinancialClientsByCategory, filters],
    () => getFinancialClientsByCategory(filters),
    { retry: false, keepPreviousData: true }
  );
}

export function useFinancialPlansByCategory(
  page: number
): UseQueryResult<IFinancialPage<IFinancialPlansByCategory>> {
  return useQuery(
    [DashboardHooks.FinancialPlansByCategory, page],
    () => getFinancialPlansByCategory(page),
    { retry: false, keepPreviousData: true }
  );
}

export function useGenerateSyncClientsMetadataReportMutation(
  options?: UseMutationOptions<{ message: string }, unknown, ISyncClientsMetadataExportPayload>
) {
  return useMutation(
    (payload: ISyncClientsMetadataExportPayload) => generateSyncClientsMetadataReport(payload),
    options
  );
}
